import {
  Stack,
  StackProps,
  SvgIconProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC, ReactElement, ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "shared/ui";
import BetaBadge from "components/atoms/BetaBadge";

import styles from "./ExportOption.styles.ts";

type TExportOptionProps = Omit<StackProps, "title"> & {
  description: ReactNode;
  icon: FC<SvgIconProps>;
  info?: ReactNode;
  isComingSoon?: boolean;
  isDisabled?: boolean;
  isPremium?: boolean;
  isSelected?: boolean;
  title: ReactNode;
  value?: string;
};

const ExportOption: FC<TExportOptionProps> = ({
  description,
  icon: Icon,
  info,
  isComingSoon,
  isDisabled,
  isPremium,
  isSelected,
  onClick,
  sx,
  title,
  value,
  ...rest
}: TExportOptionProps): ReactElement => {
  const isActionsVisible = isComingSoon || isPremium;
  const isBeta = value === "linkedInMyConnections";

  const content = (
    <Stack
      onClick={!isDisabled ? onClick : undefined}
      sx={[
        styles.wrapper({ isDisabled, isSelected }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      <Stack sx={styles.iconWrapper}>
        <Icon sx={styles.icon({ isDisabled })} />
        {isBeta && <BetaBadge />}
      </Stack>
      <Typography sx={styles.title({ isDisabled })} variant="h6">
        {title}
      </Typography>
      <Typography sx={styles.description({ isDisabled })}>
        {description}
      </Typography>
      {isActionsVisible && (
        <Stack sx={styles.info}>
          {isComingSoon && (
            <Typography sx={styles.comingSoon}>Coming soon</Typography>
          )}
          {isPremium && (
            <Button
              as={Link}
              className="btn--small"
              to="/upgrade"
              variant="primary"
            >
              Update to Pro
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );

  if (info) {
    return (
      <Tooltip
        arrow
        placement="top"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, -8],
                },
              },
            ],
            sx: styles.infoTooltip,
          },
        }}
        title={info}
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default ExportOption;
